import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Tag, Button, Divider, Space, Modal, Form, Input, InputNumber, DatePicker, Select, message } from 'antd';
import { PrinterOutlined, EditOutlined, DeleteOutlined, UndoOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import api from '../utils/api';
import { printInvoice } from '../utils/printInvoice';
import { getLastPaymentDate } from '../utils/enrollment';
import dayjs from 'dayjs';

const { Option } = Select;

const StudentDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { user } = useAuthStore();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [refundForm] = Form.useForm();

    // Edit modal state
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);

    // Refund / cashback modal state
    const [isRefundModalVisible, setIsRefundModalVisible] = useState(false);
    const [refundingInvoice, setRefundingInvoice] = useState(null);

    const { data, isLoading } = useQuery(['student', id], async () => {
        const res = await api.get(`/students/${id}`);
        return res.data.data;
    });

    // Fetch student's invoices
    const { data: invoicesData, isLoading: invoicesLoading } = useQuery(['student-invoices', id], async () => {
        const res = await api.get('/income', {
            params: { student: id, limit: 10000 }
        });
        return res.data.data;
    });

    // Update mutation
    const updateMutation = useMutation(
        ({ id, data }) => api.put(`/income/${id}`, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['student-invoices', id]);
                queryClient.invalidateQueries(['student', id]);
                message.success(t('updatedSuccessfully'));
                setIsEditModalVisible(false);
                setEditingInvoice(null);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('updateFailed'));
            },
        }
    );

    // Refund / cashback mutation
    const refundMutation = useMutation(
        ({ invoiceId, data }) => api.post(`/income/${invoiceId}/refund`, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['student-invoices', id]);
                queryClient.invalidateQueries(['student', id]);
                message.success(t('refundRecordedSuccessfully'));
                setIsRefundModalVisible(false);
                setRefundingInvoice(null);
                refundForm.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('refundFailed'));
            },
        }
    );

    // Delete mutation
    const deleteMutation = useMutation(
        (invoiceId) => api.delete(`/income/${invoiceId}`),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['student-invoices', id]);
                queryClient.invalidateQueries(['student', id]);
                message.success(t('deletedSuccessfully'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('deleteFailed'));
            },
        }
    );

    // Delete enrollment (assigned course) mutation
    const deleteEnrollmentMutation = useMutation(
        (enrollmentId) => api.delete(`/students/${id}/enrollments/${enrollmentId}`),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['student', id]);
                queryClient.invalidateQueries(['student-invoices', id]);
                message.success(t('deletedSuccessfully'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('deleteFailed'));
            },
        }
    );

    if (isLoading) return <div>{t('loading')}</div>;
    if (!data) return <div>{t('studentNotFound')}</div>;
    const student = data;

    // Helper function to format currency
    const formatCurrency = (amount, currency = 'USD') => {
        const symbol = currency === 'IQD' ? 'IQD' : '$';
        return `${symbol}${amount?.toLocaleString() || '0'}`;
    };

    const handlePrint = (record) => {
        printInvoice({
            invoice: record,
            student,
            user,
            t,
            language: i18n.language
        });
    };

    const handleEdit = (record) => {
        setEditingInvoice(record);
        form.setFieldsValue({
            amount: record.amount,
            discount: record.discount || 0,
            description: record.description,
            paymentMethod: record.paymentMethod,
            date: dayjs(record.date),
            category: record.category,
        });
        setIsEditModalVisible(true);
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: t('areYouSureDeleteInvoice'),
            okText: t('yes'),
            okType: 'danger',
            cancelText: t('no'),
            onOk: () => deleteMutation.mutate(record._id),
        });
    };

    const handleDeleteEnrollment = (record) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: t('removeCourseConfirm'),
            okText: t('yes'),
            okType: 'danger',
            cancelText: t('no'),
            onOk: () => deleteEnrollmentMutation.mutate(record._id),
        });
    };

    // Total already refunded against a payment (refund records are stored as negative amounts)
    const getRefundedAmount = (invoice) =>
        (invoicesData || [])
            .filter((inv) => inv.category === 'refund' && String(inv.refundOf) === String(invoice._id))
            .reduce((sum, inv) => sum + Math.abs(inv.amount || 0), 0);

    const handleRefund = (record) => {
        const maxRefundable = (record.amount || 0) - getRefundedAmount(record);
        setRefundingInvoice({ ...record, maxRefundable });
        refundForm.setFieldsValue({
            amount: maxRefundable,
            paymentMethod: record.paymentMethod || 'cash',
            date: dayjs(),
            reason: '',
        });
        setIsRefundModalVisible(true);
    };

    const handleRefundSubmit = (values) => {
        refundMutation.mutate({
            invoiceId: refundingInvoice._id,
            data: {
                amount: values.amount,
                paymentMethod: values.paymentMethod,
                date: values.date ? values.date.toISOString() : new Date().toISOString(),
                reason: values.reason,
            },
        });
    };

    const handleUpdateSubmit = (values) => {
        const updateData = {
            ...values,
            date: values.date.toISOString(),
        };
        updateMutation.mutate({ id: editingInvoice._id, data: updateData });
    };

    const columns = [
        { title: t('course'), key: 'course', render: (_, r) => r.course?.name || 'N/A' },
        { title: t('startDate'), dataIndex: 'startDate', key: 'startDate', render: d => d ? new Date(d).toLocaleDateString() : '' },
        { title: t('endDate'), dataIndex: 'endDate', key: 'endDate', render: d => d ? new Date(d).toLocaleDateString() : '' },
        {
            title: t('lastPaymentDate'),
            key: 'lastPaymentDate',
            render: (_, r) => {
                const lastPaymentDate = getLastPaymentDate(r.payment);
                return lastPaymentDate ? lastPaymentDate.toLocaleDateString() : '-';
            }
        },
        { title: t('status'), dataIndex: 'status', key: 'status', render: s => <Tag color={s === 'active' ? 'green' : s === 'completed' ? 'blue' : 'red'}>{s.toUpperCase()}</Tag> },
        {
            title: t('fee'),
            key: 'fee',
            render: (_, r) => {
                const course = r.course;
                const originalFee = course?.fee?.amount || 0;
                const discount = course?.discount || 0;
                const finalFee = r.payment?.totalFee || 0;
                const currency = r.payment?.currency;

                if (discount > 0 && originalFee > finalFee) {
                    return (
                        <div>
                            <div style={{ textDecoration: 'line-through', color: '#999', fontSize: '12px' }}>
                                {formatCurrency(originalFee, currency)}
                            </div>
                            <div style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                {formatCurrency(finalFee, currency)}
                            </div>
                            <Tag color="green" size="small">-{formatCurrency(discount, currency)}</Tag>
                        </div>
                    );
                }
                return formatCurrency(finalFee, currency);
            }
        },
        {
            title: t('paidAmount') || 'Paid',
            key: 'paidAmount',
            render: (_, r) => {
                const currency = r.payment?.currency;
                return <span style={{ color: '#15803d', fontWeight: 600 }}>{formatCurrency(r.payment?.paidAmount || 0, currency)}</span>;
            }
        },
        {
            title: t('discount') || 'Discount',
            key: 'discount',
            render: (_, r) => {
                const currency = r.payment?.currency;
                const discount = r.payment?.discount || 0;
                return discount > 0 ? <span style={{ color: '#2563eb', fontWeight: 600 }}>{formatCurrency(discount, currency)}</span> : '-';
            }
        },
        {
            title: t('remaining') || 'Remaining',
            key: 'remaining',
            render: (_, r) => {
                const currency = r.payment?.currency;
                const remaining = r.payment?.remainingAmount || 0;
                return <span style={{ color: remaining > 0 ? '#dc2626' : '#15803d', fontWeight: 600 }}>{formatCurrency(remaining, currency)}</span>;
            }
        },
        { title: t('paymentStatus'), key: 'paymentStatus', render: (_, r) => <Tag color={r.payment?.paymentStatus === 'completed' ? 'green' : 'orange'}>{r.payment?.paymentStatus?.toUpperCase()}</Tag> },
        {
            title: t('paymentDeadline') || 'Deadline',
            key: 'paymentDeadline',
            render: (_, r) => {
                if (r.payment?.paymentStatus === 'completed') return <Tag color="green">Paid</Tag>;
                if (!r.payment?.paymentDeadline) return '-';
                const deadline = new Date(r.payment.paymentDeadline);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                deadline.setHours(0, 0, 0, 0);
                const diffDays = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
                let color = 'blue';
                let label = `${diffDays} days left`;
                if (diffDays < 0) { color = 'red'; label = `${Math.abs(diffDays)} days overdue`; }
                else if (diffDays === 0) { color = 'red'; label = 'Today'; }
                else if (diffDays <= 3) { color = 'orange'; }
                return (
                    <div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{new Date(r.payment.paymentDeadline).toLocaleDateString()}</div>
                        <Tag color={color}>{label}</Tag>
                    </div>
                );
            }
        },
        {
            title: t('actions'),
            key: 'enrollmentActions',
            fixed: 'right',
            render: (_, record) => (
                <Button
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteEnrollment(record)}
                    size="small"
                    danger
                >
                    {t('delete')}
                </Button>
            ),
        },
    ];

    const invoiceColumns = [
        { title: t('date'), dataIndex: 'date', key: 'date', render: (date) => dayjs(date).format('YYYY-MM-DD') },
        { title: t('receiptNo'), dataIndex: 'receiptNumber', key: 'receiptNumber' },
        {
            title: t('category'),
            dataIndex: 'category',
            key: 'category',
            render: (category, record) => {
                if (category === 'refund') return <Tag color="red">{t('refund')}</Tag>;
                if (record.status === 'refunded') return <span>{category} <Tag color="orange">{t('refunded')}</Tag></span>;
                return category;
            }
        },
        { title: t('description'), dataIndex: 'description', key: 'description' },
        {
            title: t('amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount, record) => record.category === 'refund'
                ? <span style={{ color: '#dc2626', fontWeight: 600 }}>-{formatCurrency(Math.abs(amount), record.currency)}</span>
                : formatCurrency(amount, record.currency)
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => {
                const isRefund = record.category === 'refund';
                const canRefund = !isRefund && (record.amount || 0) - getRefundedAmount(record) > 0;
                return (
                    <Space>
                        <Button
                            icon={<PrinterOutlined />}
                            onClick={() => handlePrint(record)}
                            size="small"
                        >
                            {t('print')}
                        </Button>
                        {!isRefund && (
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => handleEdit(record)}
                                size="small"
                                type="primary"
                            >
                                {t('edit')}
                            </Button>
                        )}
                        {canRefund && (
                            <Button
                                icon={<UndoOutlined />}
                                onClick={() => handleRefund(record)}
                                size="small"
                                style={{ color: '#d97706', borderColor: '#d97706' }}
                            >
                                {t('refund')}
                            </Button>
                        )}
                        <Button
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                            size="small"
                            danger
                        >
                            {t('delete')}
                        </Button>
                    </Space>
                );
            },
        },
    ];

    const totalRefunded = (invoicesData || [])
        .filter((inv) => inv.category === 'refund')
        .reduce((sum, inv) => sum + Math.abs(inv.amount || 0), 0);

    return (
        <div>
            <Button onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>{t('back')}</Button>
            
            {/* Student Profile Card */}
            <Card title={`${t('student')}: ${student.firstName} ${student.lastName}`} style={{ marginBottom: 16 }}>
                
                {/* Personal Information Section */}
                <h3 style={{ color: '#1890ff', marginTop: 0 }}>{t('personalInformation') || 'Personal Information'}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px', marginBottom: 24 }}>
                    <div>
                        <strong>{t('studentId')}:</strong> {student.studentId || '-'}
                    </div>
                    <div>
                        <strong>{t('firstName') || 'First Name'}:</strong> {student.firstName || '-'}
                    </div>
                    <div>
                        <strong>{t('lastName') || 'Last Name'}:</strong> {student.lastName || '-'}
                    </div>
                    <div>
                        <strong>{t('email')}:</strong> {student.email || '-'}
                    </div>
                    <div>
                        <strong>{t('phone')}:</strong> {student.phone || '-'}
                    </div>
                    <div>
                        <strong>{t('dateOfBirth') || 'Date of Birth'}:</strong> {student.dateOfBirth ? dayjs(student.dateOfBirth).format('YYYY-MM-DD') : '-'}
                    </div>
                    <div>
                        <strong>{t('gender') || 'Gender'}:</strong> {student.gender ? <Tag color="blue">{student.gender.toUpperCase()}</Tag> : '-'}
                    </div>
                    <div>
                        <strong>{t('status')}:</strong> <Tag color={student.status === 'active' ? 'green' : student.status === 'graduated' ? 'blue' : 'red'}>{student.status?.toUpperCase() || '-'}</Tag>
                    </div>
                    <div>
                        <strong>{t('createdAt')}:</strong> {student.createdAt ? dayjs(student.createdAt).format('YYYY-MM-DD HH:mm') : '-'}
                    </div>
                </div>

                {/* Photo Section */}
                {student.photo && (
                    <>
                        <h3 style={{ color: '#1890ff' }}>{t('photo') || 'Photo'}</h3>
                        <div style={{ marginBottom: 24 }}>
                            <img 
                                src={`${api.defaults.baseURL}${student.photo}`} 
                                alt={`${student.firstName} ${student.lastName}`} 
                                style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: '2px solid #d9d9d9' }}
                            />
                        </div>
                    </>
                )}

                {/* Academic Information Section */}
                <h3 style={{ color: '#1890ff' }}>{t('academicInformation') || 'Academic Information'}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px', marginBottom: 24 }}>
                    <div>
                        <strong>{t('degree') || 'Degree'}:</strong> {student.degree || '-'}
                    </div>
                    <div>
                        <strong>{t('school')}:</strong> {student.school || '-'}
                    </div>
                    <div>
                        <strong>{t('studyLanguage')}:</strong> {student.studyLanguage ? <Tag color="geekblue">{t(student.studyLanguage)}</Tag> : '-'}
                    </div>
                    <div>
                        <strong>{t('studyingType') || 'Studying Type'}:</strong> {student.studyingType ? <Tag color="purple">{student.studyingType}</Tag> : '-'}
                    </div>
                    <div>
                        <strong>{t('classStartHour') || 'Class Start Hour'}:</strong> {student.classStartHour || '-'}
                    </div>
                    <div>
                        <strong>{t('classEndHour') || 'Class End Hour'}:</strong> {student.classEndHour || '-'}
                    </div>
                    <div>
                        <strong>{t('howYouHeardUs') || 'How You Heard About Us'}:</strong> {student.howYouHeardUs ? <Tag color="orange">{student.howYouHeardUs}</Tag> : '-'}
                    </div>
                </div>

                {/* Address Information Section */}
                {student.address && (
                    <>
                        <h3 style={{ color: '#1890ff' }}>{t('address') || 'Address'}</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px', marginBottom: 24 }}>
                            <div>
                                <strong>{t('street') || 'Street'}:</strong> {student.address.street || '-'}
                            </div>
                            <div>
                                <strong>{t('city') || 'City'}:</strong> {student.address.city || '-'}
                            </div>
                            <div>
                                <strong>{t('state') || 'State'}:</strong> {student.address.state || '-'}
                            </div>
                            <div>
                                <strong>{t('country') || 'Country'}:</strong> {student.address.country || '-'}
                            </div>
                            <div>
                                <strong>{t('zipCode') || 'ZIP Code'}:</strong> {student.address.zipCode || '-'}
                            </div>
                        </div>
                    </>
                )}

                {/* Emergency Contact Section */}
                {student.emergencyContact && (
                    <>
                        <h3 style={{ color: '#1890ff' }}>{t('emergencyContact') || 'Emergency Contact'}</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px', marginBottom: 24 }}>
                            <div>
                                <strong>{t('name') || 'Name'}:</strong> {student.emergencyContact.name || '-'}
                            </div>
                            <div>
                                <strong>{t('emergencyPhoneOwner')}:</strong> {student.emergencyContact.relationship ? t(student.emergencyContact.relationship, { defaultValue: student.emergencyContact.relationship, nsSeparator: false, keySeparator: false }) : '-'}
                            </div>
                            <div>
                                <strong>{t('phone')}:</strong> {student.emergencyContact.phone || '-'}
                            </div>
                        </div>
                    </>
                )}

                {/* Notes Section */}
                {student.notes && (
                    <>
                        <h3 style={{ color: '#1890ff' }}>{t('notes') || 'Notes'}</h3>
                        <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '4px', marginBottom: 24 }}>
                            {student.notes}
                        </div>
                    </>
                )}

                {/* Enrollments Section */}
                <h3 style={{ color: '#1890ff' }}>{t('enrollments') || 'Enrollments'}</h3>
                
                {/* Total Enrollments Count */}
                <div style={{ marginBottom: 16 }}>
                    <Tag color="geekblue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                        {t('totalEnrollments') || 'Total Enrollments'}: {student.enrollments?.length || 0}
                    </Tag>
                </div>

                {/* Current/Active Course */}
                <h4 style={{ color: '#52c41a' }}>{t('currentCourse') || 'Current Course'}</h4>
                <Table
                    columns={columns}
                    dataSource={student.enrollments?.filter(e => e.status === 'active' || e.status === 'suspended') || []}
                    rowKey={(_, i) => `active-${i}`}
                    pagination={false}
                    locale={{ emptyText: t('noActiveCourse') || 'No active course' }}
                    scroll={{ x: 'max-content' }}
                    style={{ marginBottom: 24 }}
                />

                {/* Previous Courses */}
                {student.enrollments?.some(e => e.status === 'cancelled' || e.status === 'completed') && (
                    <>
                        <h4 style={{ color: '#8c8c8c' }}>{t('previousCourses') || 'Previous Courses'}</h4>
                        <Table
                            columns={columns}
                            dataSource={student.enrollments?.filter(e => e.status === 'cancelled' || e.status === 'completed') || []}
                            rowKey={(_, i) => `prev-${i}`}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            style={{ marginBottom: 24 }}
                        />
                    </>
                )}

                <Divider />

                {/* Payment History Section */}
                <h3 style={{ color: '#1890ff' }}>{t('paymentHistory')}</h3>
                
                {/* Total Payments Summary */}
                {invoicesData && invoicesData.length > 0 && (
                    <div style={{ marginBottom: 16, display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <Tag color="success" style={{ fontSize: '14px', padding: '4px 12px' }}>
                            {t('totalPayments') || 'Total Payments'}: {invoicesData.length}
                        </Tag>
                        <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                            {t('totalAmount') || 'Total Amount'}: {formatCurrency(
                                invoicesData.reduce((sum, inv) => sum + (inv.amount || 0), 0),
                                invoicesData[0]?.currency
                            )}
                        </Tag>
                        {totalRefunded > 0 && (
                            <Tag color="red" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                {t('totalRefunded')}: {formatCurrency(totalRefunded, invoicesData[0]?.currency)}
                            </Tag>
                        )}
                    </div>
                )}
                
                <Table
                    columns={invoiceColumns}
                    dataSource={invoicesData || []}
                    rowKey="_id"
                    loading={invoicesLoading}
                    pagination={false}
                    locale={{ emptyText: t('noInvoicesFound') }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>

            {/* Refund / Cashback Modal */}
            <Modal
                title={t('refundInvoice')}
                open={isRefundModalVisible}
                onCancel={() => {
                    setIsRefundModalVisible(false);
                    setRefundingInvoice(null);
                    refundForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                {refundingInvoice && (
                    <>
                        <div style={{
                            background: '#fffbeb',
                            border: '2px solid #fcd34d',
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '24px'
                        }}>
                            <div style={{ fontSize: '12px', color: '#92400e', fontWeight: 600, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {t('receiptNo')}: {refundingInvoice.receiptNumber}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                                <div style={{ background: 'white', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
                                        {t('paidInThisInvoice')}
                                    </div>
                                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#15803d' }}>
                                        {formatCurrency(refundingInvoice.amount || 0, refundingInvoice.currency)}
                                    </div>
                                </div>
                                <div style={{ background: 'white', border: '2px solid #fca5a5', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '11px', color: '#991b1b', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
                                        {t('alreadyRefunded')}
                                    </div>
                                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#dc2626' }}>
                                        {formatCurrency((refundingInvoice.amount || 0) - refundingInvoice.maxRefundable, refundingInvoice.currency)}
                                    </div>
                                </div>
                                <div style={{ background: 'white', border: '2px solid #fcd34d', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '11px', color: '#92400e', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
                                        {t('maxRefundable')}
                                    </div>
                                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#d97706' }}>
                                        {formatCurrency(refundingInvoice.maxRefundable, refundingInvoice.currency)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <Form form={refundForm} layout="vertical" onFinish={handleRefundSubmit}>
                            <Form.Item
                                name="amount"
                                label={t('refundAmount')}
                                rules={[
                                    { required: true, message: t('pleaseEnterRefundAmount') },
                                    {
                                        validator: (_, value) => {
                                            if (value === undefined || value === null) return Promise.resolve();
                                            if (value <= 0) return Promise.reject(new Error(t('pleaseEnterRefundAmount')));
                                            if (value > refundingInvoice.maxRefundable) {
                                                return Promise.reject(new Error(t('refundExceedsPaid')));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    max={refundingInvoice.maxRefundable}
                                    prefix={refundingInvoice.currency === 'IQD' ? 'IQD' : '$'}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>

                            <Form.Item
                                name="paymentMethod"
                                label={t('refundMethod')}
                                rules={[{ required: true, message: t('pleaseSelectPaymentMethod') }]}
                            >
                                <Select placeholder={t('selectPaymentMethod')}>
                                    <Option value="cash">{t('cash')}</Option>
                                    <Option value="online">{t('onlinePayment')}</Option>
                                    <Option value="bank-transfer">{t('bankTransfer')}</Option>
                                    <Option value="card">{t('card')}</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="date"
                                label={t('date')}
                                rules={[{ required: true, message: t('pleaseSelectDate') }]}
                            >
                                <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
                            </Form.Item>

                            <Form.Item name="reason" label={t('refundReason')}>
                                <Input.TextArea rows={3} placeholder={t('enterRefundReason')} />
                            </Form.Item>

                            <Form.Item>
                                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                    <Button onClick={() => {
                                        setIsRefundModalVisible(false);
                                        setRefundingInvoice(null);
                                        refundForm.resetFields();
                                    }}>
                                        {t('cancel')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        danger
                                        htmlType="submit"
                                        loading={refundMutation.isLoading}
                                    >
                                        {t('confirmRefund')}
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Modal>

            {/* Edit Invoice Modal */}
            <Modal
                title={t('editInvoice')}
                open={isEditModalVisible}
                onCancel={() => {
                    setIsEditModalVisible(false);
                    setEditingInvoice(null);
                    form.resetFields();
                }}
                footer={null}
                width={700}
            >
                {editingInvoice && (
                    <>
                        {/* Payment Summary Section */}
                        <div style={{
                            background: '#f8fafc',
                            border: '2px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '24px'
                        }}>
                            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {t('paymentSummary')}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                                <div style={{
                                    background: 'white',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
                                        {t('courseFee')}
                                    </div>
                                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                                        {formatCurrency(editingInvoice.courseFee || 0, editingInvoice.currency)}
                                    </div>
                                </div>
                                <div style={{
                                    background: 'white',
                                    border: '2px solid #fca5a5',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{ fontSize: '11px', color: '#991b1b', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
                                        {t('discount')}
                                    </div>
                                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#dc2626' }}>
                                        {formatCurrency(editingInvoice.discount || 0, editingInvoice.currency)}
                                    </div>
                                </div>
                                <div style={{
                                    background: 'white',
                                    border: '2px solid #86efac',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{ fontSize: '11px', color: '#166534', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
                                        {t('paidInThisInvoice')}
                                    </div>
                                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#15803d' }}>
                                        {formatCurrency(editingInvoice.amount || 0, editingInvoice.currency)}
                                    </div>
                                </div>
                            </div>
                            {editingInvoice.remainingBalance !== undefined && (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '12px',
                                    background: 'white',
                                    border: '2px solid #3b82f6',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{ fontSize: '11px', color: '#1e40af', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase' }}>
                                        {t('remainingBalance')}
                                    </div>
                                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#2563eb' }}>
                                        {formatCurrency(editingInvoice.remainingBalance || 0, editingInvoice.currency)}
                                    </div>
                                </div>
                            )}
                        </div>

                        <Divider style={{ margin: '24px 0' }} />

                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleUpdateSubmit}
                        >
                            <Form.Item
                                name="amount"
                                label={t('paidAmount')}
                                rules={[{ required: true, message: t('pleaseEnterAmount') }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    placeholder={t('amount')}
                                    prefix={editingInvoice.currency === 'IQD' ? 'IQD' : '$'}
                                />
                            </Form.Item>

                            <Form.Item
                                name="discount"
                                label={t('discount')}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    placeholder={t('discount')}
                                    prefix={editingInvoice.currency === 'IQD' ? 'IQD' : '$'}
                                />
                            </Form.Item>

                            <Form.Item
                                name="paymentMethod"
                                label={t('paymentMethod')}
                                rules={[{ required: true, message: t('pleaseSelectPaymentMethod') }]}
                            >
                                <Select placeholder={t('selectPaymentMethod')}>
                                    <Option value="cash">{t('cash')}</Option>
                                    <Option value="online">{t('onlinePayment')}</Option>
                                    <Option value="bank-transfer">{t('bankTransfer')}</Option>
                                    <Option value="card">{t('card')}</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="date"
                                label={t('date')}
                                rules={[{ required: true, message: t('pleaseSelectDate') }]}
                            >
                                <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
                            </Form.Item>

                            <Form.Item
                                name="category"
                                label={t('category')}
                            >
                                <Input placeholder={t('category')} />
                            </Form.Item>

                            <Form.Item
                                name="description"
                                label={t('description')}
                            >
                                <Input.TextArea rows={3} placeholder={t('description')} />
                            </Form.Item>

                            <Form.Item>
                                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                    <Button onClick={() => {
                                        setIsEditModalVisible(false);
                                        setEditingInvoice(null);
                                        form.resetFields();
                                    }}>
                                        {t('cancel')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={updateMutation.isLoading}
                                    >
                                        {t('update')}
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default StudentDetails;
